import { Injectable, OnDestroy } from '@angular/core';

import { from, Observable, Subject, timer } from 'rxjs';

import { map, shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';

import { EnableClassTelemetry } from 'app/services/otel.service';
import { ReportService } from '../../../providers/storage-api/report.service';

const REFRESH_INTERVAL = 60000;

@EnableClassTelemetry()
@Injectable({
  providedIn: 'root',
})
export class TagCacheService implements OnDestroy {
  private tags$: Map<string, Observable<{ title: string; count: number }[]>>;
  private unsubs$: Map<string, Subject<void>>;

  constructor(private reportService: ReportService) {
    this.tags$ = new Map();
    this.unsubs$ = new Map();
  }

  public initCache(
    collectionName: string,
    analysisName: string,
    projectId: string
  ): Observable<{ title: string; count: number }[]> {
    if (!this.tags$.has(collectionName)) {
      const unsub$ = new Subject<void>();
      this.unsubs$.set(collectionName, unsub$);

      // Create a timer...
      const timer$ = timer(0, REFRESH_INTERVAL);

      // ...that makes an http request to fetch data on each tick
      const timerSub$ = timer$.pipe(
        switchMap((_) => this.loadTags(collectionName, analysisName, projectId)),
        map((response) =>
          Object.keys(response.items.count).map((key) => ({
            title: key,
            count: response.items.count[key],
          }))
        ),
        takeUntil(unsub$),
        shareReplay(1)
      );

      // We are caching the observables themselves
      this.tags$.set(collectionName, timerSub$);

      // Subscribe to fire first request right at init time
      timerSub$.subscribe();

      return timerSub$;
    }

    return this.tags$.get(collectionName);
  }

  public getTags(
    collectionName: string,
    analysisName: string,
    projectId: string,
    prefix?: string
  ): Observable<{ title: string; count: number }[]> {
    return this.initCache(collectionName, analysisName, projectId).pipe(
      map((response) => {
        if (prefix) {
          return response.filter((item) => item.title.startsWith(prefix));
        }
        return response;
      })
    );
  }

  public getTagsImmediate(
    collectionName: string,
    analysisName: string,
    projectId: string,
    prefix?: string
  ): Promise<{ title: string; count: number }[]> {
    return from(this.getTags(collectionName, analysisName, projectId, prefix))
      .pipe(take(1))
      .toPromise();
  }

  public reload(collectionName: string) {
    this.unsubs$.get(collectionName)?.next();

    this.tags$.delete(collectionName);
  }

  public reloadAll() {
    this.unsubs$.forEach((unsub$) => unsub$.next());

    this.unsubs$.clear();
    this.tags$.clear();
  }

  private loadTags(
    collectionName: string,
    analysisName: string,
    projectId: string
  ): Observable<any> {
    return from(
      this.reportService.getReport(
        collectionName,
        undefined,
        'metadataTopFeatures',
        {
          analysisName,
          featureName: 'tags',
          standardField: 'tags',
          topN: 9999,
        },
        'json',
        null,
        null,
        null,
        projectId
      )
    );
  }

  ngOnDestroy() {
    this.reloadAll();
  }
}
